import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  AlertCircle,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  CircleDollarSign,
  Clock3,
  Mail,
  RefreshCw,
  Search,
  User,
  Users,
  Wallet,
  XCircle,
} from "lucide-react";

const Maas = () => {
  const [data, setData] = useState([]);
  const [allPeriods, setAllPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [filteredPeriodSalaries, setFilteredPeriodSalaries] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const [salaryData, setSalaryData] = useState(null);

  const [personalLoading, setPersonalLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);

  const [personalError, setPersonalError] = useState(null);
  const [usersError, setUsersError] = useState(null);

  const [retryPersonal, setRetryPersonal] = useState(0);
  const [retryUsers, setRetryUsers] = useState(0);

  const { userInfo } = useSelector((state) => state.auth);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // ---------------------------------------------------------
  // XƏTA MƏTNİNİ HAZIRLA
  // ---------------------------------------------------------
  const getErrorMessage = (err, defaultMessage) => {
    if (!err) {
      return defaultMessage;
    }

    if (err.response) {
      const serverMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.response?.data?.details;

      if (serverMessage) {
        return String(serverMessage);
      }

      if (err.response.status === 400) {
        return "Göndərilən sorğu düzgün deyil.";
      }

      if (err.response.status === 401) {
        return "Sessiyanızın müddəti bitib. Zəhmət olmasa yenidən daxil olun.";
      }

      if (err.response.status === 403) {
        return "Bu məlumatlara baxmaq üçün icazəniz yoxdur.";
      }

      if (err.response.status === 404) {
        return "Məlumat tapılmadı.";
      }

      if (err.response.status === 408) {
        return "Serverdən cavab almaq çox uzun çəkdi.";
      }

      if (err.response.status === 429) {
        return "Çox sayda sorğu göndərildi. Bir qədər sonra yenidən cəhd edin.";
      }

      if (err.response.status >= 500) {
        return `Server xətası baş verdi. Xəta kodu: ${err.response.status}`;
      }

      return `Sorğu zamanı xəta baş verdi. Xəta kodu: ${err.response.status}`;
    }

    if (err.request) {
      return "Serverə qoşulmaq mümkün olmadı. İnternet bağlantınızı və serverin işlək vəziyyətdə olduğunu yoxlayın.";
    }

    if (err.message) {
      return err.message;
    }

    return defaultMessage;
  };

  // ---------------------------------------------------------
  // ŞƏXSİ MAAŞ MƏLUMATLARINI GƏTİR
  // ---------------------------------------------------------
  const fetchSalaryData = useCallback(async () => {
    if (!userInfo?.referralCode) {
      setPersonalError(
        "İstifadəçinin referans kodu tapılmadı. Maaş məlumatlarını göstərmək mümkün deyil."
      );
      setSalaryData(null);
      return;
    }

    if (!API_BASE_URL) {
      setPersonalError(
        "API serverinin ünvanı müəyyən edilməyib. REACT_APP_API_BASE_URL dəyişənini yoxlayın."
      );
      setSalaryData(null);
      return;
    }

    setPersonalLoading(true);
    setPersonalError(null);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/users/salary/${userInfo.referralCode}`,
        {
          withCredentials: true,
        }
      );

      setSalaryData(response.data || null);
    } catch (err) {
      const message = getErrorMessage(
        err,
        "Şəxsi maaş məlumatları alınarkən xəta baş verdi."
      );

      setPersonalError(message);
      setSalaryData(null);
    } finally {
      setPersonalLoading(false);
    }
  }, [userInfo?.referralCode, API_BASE_URL]);

  useEffect(() => {
    fetchSalaryData();
  }, [fetchSalaryData, retryPersonal]);

  // ---------------------------------------------------------
  // BÜTÜN İSTİFADƏÇİLƏRİN MAAŞ MƏLUMATLARINI GƏTİR
  // ---------------------------------------------------------
  const fetchData = useCallback(async () => {
    if (!API_BASE_URL) {
      setUsersError(
        "API serverinin ünvanı müəyyən edilməyib. REACT_APP_API_BASE_URL dəyişənini yoxlayın."
      );
      setData([]);
      return;
    }

    setUsersLoading(true);
    setUsersError(null);

    try {
      const response = await axios.get(`${API_BASE_URL}/api/mine`, {
        withCredentials: true,
      });

      const users = Array.isArray(response.data) ? response.data : [];

      setData(users);

      // Bütün periodları təhlükəsiz şəkildə topla
      const all = users.flatMap((user) =>
        Array.isArray(user?.periodSalaries)
          ? user.periodSalaries
              .map((period) => period?.periodLabel)
              .filter(Boolean)
          : []
      );

      const uniquePeriods = [...new Set(all)];

      setAllPeriods(uniquePeriods);

      // Əgər hazırda seçilmiş period yoxdursa,
      // ilk periodu seç
      setSelectedPeriod((currentPeriod) => {
        if (
          currentPeriod &&
          uniquePeriods.includes(currentPeriod)
        ) {
          return currentPeriod;
        }

        return uniquePeriods.length > 0 ? uniquePeriods[0] : "";
      });
    } catch (err) {
      const message = getErrorMessage(
        err,
        "İstifadəçilərin maaş məlumatları alınarkən xəta baş verdi."
      );

      setUsersError(message);
      setData([]);
      setAllPeriods([]);
      setSelectedPeriod("");
    } finally {
      setUsersLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchData();
  }, [fetchData, retryUsers]);

  // ---------------------------------------------------------
  // SEÇİLMİŞ PERIOD ÜZRƏ MAAŞLARI FİLTRLƏ
  // ---------------------------------------------------------
useEffect(() => {
  if (!selectedPeriod || data.length === 0) {
    setFilteredPeriodSalaries([]);
    return;
  }

  const filtered = data
    .map((user) => {
      const periodSalaries = Array.isArray(user?.periodSalaries)
        ? user.periodSalaries
        : [];

      const matching = periodSalaries.find(
        (period) => period?.periodLabel === selectedPeriod
      );

      if (!matching) {
        return null;
      }

      return {
        ...matching,

        // Əvvəl periodSalaries daxilindəki məlumatı götürür
        name:
          matching?.name ||
          user?.name ||
          "Ad göstərilməyib",

        email:
          matching?.email ||
          user?.email ||
          "E-poçt göstərilməyib",

        referralCode:
          matching?.referralCode ||
          user?.referralCode ||
          "",

        photo:
          matching?.photo ||
          user?.photo ||
          "",

        rank:
          matching?.rank ||
          user?.rank ||
          "Rütbə göstərilməyib",

        // Seçilmiş dövrün maaşı
        totalUserSalary:
          matching?.salary ??
          user?.salary ??
          0,
      };
    })
    .filter(Boolean);

  setFilteredPeriodSalaries(filtered);
}, [selectedPeriod, data]);


  // ---------------------------------------------------------
  // AXTARIŞ
  // ---------------------------------------------------------
  const filteredSalaries = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return filteredPeriodSalaries;
    }

    return filteredPeriodSalaries.filter((user) => {
      const name = String(user?.name || "").toLowerCase();
      const email = String(user?.email || "").toLowerCase();
      const referralCode = String(user?.referralCode || "").toLowerCase();

      return (
        name.includes(query) ||
        email.includes(query) ||
        referralCode.includes(query)
      );
    });
  }, [filteredPeriodSalaries, searchQuery]);

  // ---------------------------------------------------------
  // FORMATLAMA
  // ---------------------------------------------------------
  const formatSalary = (value) => {
    const number = Number(value ?? 0);

    if (Number.isNaN(number)) {
      return "0.00";
    }

    return number.toFixed(2);
  };

  const getPhoto = (photo) => {
    if (!photo) {
      return null;
    }

    if (Array.isArray(photo)) {
      return photo[0] || null;
    }

    return photo;
  };

  // ---------------------------------------------------------
  // STATİSTİKALAR
  // ---------------------------------------------------------
  const totalSalary = useMemo(() => {
    return filteredSalaries.reduce((sum, user) => {
      return sum + Number(user?.totalUserSalary || 0);
    }, 0);
  }, [filteredSalaries]);

  // ---------------------------------------------------------
  // YÜKLƏNMƏ EKRANI
  // ---------------------------------------------------------
  const isInitialLoading =
    personalLoading && !salaryData && usersLoading && data.length === 0;

  if (isInitialLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-3xl p-8 shadow-2xl text-center">
            <div className="mx-auto mb-5 w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <Wallet className="w-8 h-8 text-white animate-pulse" />
            </div>

            <h2 className="text-xl font-bold mb-2">
              Maaş məlumatları yüklənir
            </h2>

            <p className="text-sm text-slate-500 dark:text-slate-400">
              Şəxsi və ümumi maaş məlumatlarınız hazırlanır. Zəhmət olmasa
              gözləyin...
            </p>

            <div className="mt-6 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
              <div className="h-full w-1/2 bg-gradient-to-r from-indigo-500 to-violet-600 rounded-full animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white transition-colors duration-300 px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <div className="max-w-7xl mx-auto space-y-6 sm:space-y-8">

        {/* ---------------------------------------------------
            BAŞLIQ
        --------------------------------------------------- */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700 p-6 sm:p-8 text-white shadow-2xl">
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-20 w-72 h-72 bg-fuchsia-400/10 rounded-full blur-3xl" />

          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs font-semibold mb-3">
                <CircleDollarSign className="w-4 h-4" />
                Maaş məlumatları
              </div>

              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight">
                Maaş paneli
              </h1>

              <p className="mt-2 text-sm sm:text-base text-white/75 max-w-2xl">
                Şəxsi maaşınızı və seçilmiş dövr üzrə istifadəçilərin maaş
                məlumatlarını burada izləyə bilərsiniz.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center">
                <Wallet className="w-7 h-7" />
              </div>
            </div>
          </div>
        </div>

        {/* ---------------------------------------------------
            ŞƏXSİ MAAŞ PANELİ
        --------------------------------------------------- */}
        <section className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-white/10 shadow-xl overflow-hidden">
          <div className="p-5 sm:p-6 border-b border-slate-200 dark:border-white/10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl font-bold">
                    Şəxsi maaş məlumatları
                  </h2>

                  <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                    Hesabınıza aid maaş məlumatları
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setRetryPersonal((value) => value + 1)}
                disabled={personalLoading}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold text-sm"
              >
                <RefreshCw
                  className={`w-4 h-4 ${
                    personalLoading ? "animate-spin" : ""
                  }`}
                />
                Yenilə
              </button>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            {personalLoading ? (
              <div className="flex flex-col items-center justify-center py-14 text-center">
                <div className="w-14 h-14 rounded-2xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center mb-4">
                  <RefreshCw className="w-7 h-7 text-indigo-600 dark:text-indigo-400 animate-spin" />
                </div>

                <h3 className="font-bold text-lg">
                  Şəxsi maaş məlumatları yüklənir
                </h3>

                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 max-w-md">
                  Maaş məlumatlarınız serverdən alınır. Zəhmət olmasa
                  gözləyin...
                </p>
              </div>
            ) : personalError ? (
              <div className="rounded-2xl border border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10 p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 shrink-0 rounded-xl bg-red-100 dark:bg-red-500/10 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                  </div>

                  <div className="min-w-0">
                    <h3 className="font-bold text-red-800 dark:text-red-300">
                      Maaş məlumatları yüklənmədi
                    </h3>

                    <p className="mt-1 text-sm text-red-700 dark:text-red-400 break-words">
                      Səbəb: {personalError}
                    </p>

                    <button
                      type="button"
                      onClick={() => setRetryPersonal((value) => value + 1)}
                      className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Yenidən cəhd et
                    </button>
                  </div>
                </div>
              </div>
            ) : !salaryData ? (
              <div className="rounded-2xl border border-amber-200 dark:border-amber-500/20 bg-amber-50 dark:bg-amber-500/10 p-6 text-center">
                <Clock3 className="w-8 h-8 mx-auto text-amber-600 dark:text-amber-400 mb-3" />

                <h3 className="font-bold text-amber-800 dark:text-amber-300">
                  Maaş məlumatı mövcud deyil
                </h3>

                <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">
                  Hazırda hesabınız üçün göstəriləcək maaş məlumatı tapılmadı.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-white/10">
                <table className="w-full min-w-[760px] text-sm text-left">
                  <thead className="bg-slate-50 dark:bg-slate-800/80">
                    <tr>
                      <th className="p-4 font-bold">Foto</th>
                      <th className="p-4 font-bold">Ad və soyad</th>
                      <th className="p-4 font-bold">Dövr</th>
                      <th className="p-4 font-bold">Rütbə</th>
                      <th className="p-4 font-bold">Maaş</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr className="hover:bg-slate-50 dark:hover:bg-white/5 transition">
                      <td className="p-4">
                        {getPhoto(salaryData.photo) ? (
                          <img
                            src={getPhoto(salaryData.photo)}
                            alt={salaryData.name || "İstifadəçi şəkli"}
                            className="w-12 h-12 rounded-full object-cover border-2 border-slate-200 dark:border-slate-700"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white">
                            <User className="w-5 h-5" />
                          </div>
                        )}
                      </td>

                      <td className="p-4 font-semibold">
                        {salaryData.name || "Ad göstərilməyib"}
                      </td>

                      <td className="p-4">
                        <span className="inline-flex items-center gap-2">
                          <CalendarDays className="w-4 h-4 text-indigo-500" />
                          {salaryData.periodLabel || "Dövr göstərilməyib"}
                        </span>
                      </td>

                      <td className="p-4">
                        {salaryData.rank || "Rütbə göstərilməyib"}
                      </td>

                      <td className="p-4">
                        <span className="inline-flex items-center gap-2 font-bold text-emerald-600 dark:text-emerald-400">
                          <CircleDollarSign className="w-4 h-4" />
                          {formatSalary(salaryData?.salary)} ₼
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {/* ---------------------------------------------------
            STATİSTİKALAR
        --------------------------------------------------- */}
        {!usersError && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-2xl p-5 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                    Ümumi istifadəçi sayı
                  </p>

                  <p className="mt-2 text-2xl font-black">
                    {usersLoading ? "..." : data.length}
                  </p>
                </div>

                <div className="w-11 h-11 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-2xl p-5 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                    Mövcud dövrlər
                  </p>

                  <p className="mt-2 text-2xl font-black">
                    {usersLoading ? "..." : allPeriods.length}
                  </p>
                </div>

                <div className="w-11 h-11 rounded-xl bg-violet-50 dark:bg-violet-500/10 flex items-center justify-center">
                  <CalendarDays className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-2xl p-5 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                    Seçilmiş dövr üzrə maaş
                  </p>

                  <p className="mt-2 text-2xl font-black text-emerald-600 dark:text-emerald-400">
                    {usersLoading ? "..." : `${formatSalary(totalSalary)} ₼`}
                  </p>
                </div>

                <div className="w-11 h-11 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ---------------------------------------------------
            FİLTR VƏ AXTARIŞ
        --------------------------------------------------- */}
        <section className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-white/10 shadow-xl p-4 sm:p-6">
          <div className="flex flex-col lg:flex-row gap-4">

            {/* PERIOD */}
            <div className="relative w-full lg:w-72">
              <CalendarDays className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />

              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                disabled={usersLoading || allPeriods.length === 0}
                className="w-full appearance-none pl-12 pr-10 py-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 outline-none focus:ring-2 focus:ring-indigo-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {allPeriods.length === 0 ? (
                  <option value="">
                    Dövr mövcud deyil
                  </option>
                ) : (
                  allPeriods.map((period, index) => (
                    <option key={`${period}-${index}`} value={period}>
                      {period}
                    </option>
                  ))
                )}
              </select>

              <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
            </div>

            {/* AXTARIŞ */}
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />

              <input
                type="text"
                placeholder="Ad, e-poçt və ya referans kodu ilə axtar..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 outline-none focus:ring-2 focus:ring-indigo-500 transition placeholder:text-slate-400"
              />
            </div>
          </div>

          {searchQuery && (
            <div className="mt-4 flex items-center justify-between gap-3 text-sm">
              <p className="text-slate-500 dark:text-slate-400">
                Axtarış nəticəsi:{" "}
                <span className="font-bold text-slate-900 dark:text-white">
                  {filteredSalaries.length}
                </span>
              </p>

              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="text-indigo-600 dark:text-indigo-400 font-semibold hover:underline"
              >
                Axtarışı təmizlə
              </button>
            </div>
          )}
        </section>

        {/* ---------------------------------------------------
            BÜTÜN İSTİFADƏÇİLƏRİN MAAŞLARI
        --------------------------------------------------- */}
        <section className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-white/10 shadow-xl overflow-hidden">

          <div className="p-5 sm:p-6 border-b border-slate-200 dark:border-white/10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-violet-50 dark:bg-violet-500/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                </div>

                <div>
                  <h2 className="text-lg sm:text-xl font-bold">
                    İstifadəçilərin maaş məlumatları
                  </h2>

                  <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                    Seçilmiş dövr üzrə maaş məlumatları
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setRetryUsers((value) => value + 1)}
                disabled={usersLoading}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold text-sm"
              >
                <RefreshCw
                  className={`w-4 h-4 ${
                    usersLoading ? "animate-spin" : ""
                  }`}
                />
                Məlumatları yenilə
              </button>
            </div>
          </div>

          <div className="p-4 sm:p-6">

            {/* ÜMUMİ YÜKLƏNMƏ */}
            {usersLoading ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-2xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center mb-5">
                  <Users className="w-8 h-8 text-indigo-600 dark:text-indigo-400 animate-pulse" />
                </div>

                <h3 className="text-lg font-bold">
                  İstifadəçilərin maaş məlumatları yüklənir
                </h3>

                <p className="mt-2 max-w-md text-sm text-slate-500 dark:text-slate-400">
                  Bütün istifadəçilərin məlumatları serverdən alınır.
                  Zəhmət olmasa gözləyin...
                </p>

                <div className="mt-5 w-full max-w-sm h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                  <div className="h-full w-1/2 rounded-full bg-gradient-to-r from-indigo-500 to-violet-600 animate-pulse" />
                </div>
              </div>
            ) : usersError ? (
              /* XƏTA */
              <div className="rounded-2xl border border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10 p-6">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 shrink-0 rounded-xl bg-red-100 dark:bg-red-500/10 flex items-center justify-center">
                    <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                  </div>

                  <div className="min-w-0">
                    <h3 className="text-lg font-bold text-red-800 dark:text-red-300">
                      Maaş məlumatları yüklənmədi
                    </h3>

                    <p className="mt-2 text-sm text-red-700 dark:text-red-400 break-words">
                      <span className="font-bold">Xətanın səbəbi:</span>{" "}
                      {usersError}
                    </p>

                    <p className="mt-2 text-xs text-red-600/80 dark:text-red-400/80">
                      Serverə qoşulma, icazə və ya məlumatların mövcudluğu ilə
                      bağlı problem ola bilər.
                    </p>

                    <button
                      type="button"
                      onClick={() => setRetryUsers((value) => value + 1)}
                      className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Yenidən cəhd et
                    </button>
                  </div>
                </div>
              </div>
            ) : data.length === 0 ? (
              /* MƏLUMAT YOXDUR */
              <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-slate-800/50 p-10 text-center">
                <Users className="w-10 h-10 mx-auto text-slate-400 mb-4" />

                <h3 className="text-lg font-bold">
                  İstifadəçi məlumatı yoxdur
                </h3>

                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                  Hazırda maaş məlumatları göstərilə bilən istifadəçi
                  tapılmadı.
                </p>
              </div>
            ) : allPeriods.length === 0 ? (
              /* PERIOD YOXDUR */
              <div className="rounded-2xl border border-amber-200 dark:border-amber-500/20 bg-amber-50 dark:bg-amber-500/10 p-10 text-center">
                <CalendarDays className="w-10 h-10 mx-auto text-amber-600 dark:text-amber-400 mb-4" />

                <h3 className="text-lg font-bold text-amber-800 dark:text-amber-300">
                  Maaş dövrü mövcud deyil
                </h3>

                <p className="mt-2 text-sm text-amber-700 dark:text-amber-400 max-w-md mx-auto">
                  İstifadəçilər üçün hələ heç bir maaş dövrü yaradılmayıb.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-white/10">
                <div className="max-h-[600px] overflow-y-auto">
                  <table className="w-full min-w-[950px] text-sm">
                    <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-800">
                      <tr className="text-left">
                        <th className="p-4 font-bold">№</th>
                        <th className="p-4 font-bold">Foto</th>
                        <th className="p-4 font-bold">Ad və soyad</th>
                        <th className="p-4 font-bold">E-poçt</th>
                        <th className="p-4 font-bold">Rütbə</th>
                        <th className="p-4 font-bold">Ümumi maaş</th>
                      </tr>
                    </thead>

                    <tbody>
                      {filteredSalaries.length > 0 ? (
                        filteredSalaries.map((user, index) => (
                          <tr
                            key={
                              user?._id ||
                              user?.referralCode ||
                              `${user?.email}-${index}`
                            }
                            className="border-t border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-white/5 transition"
                          >
                            <td className="p-4 font-bold text-slate-500 dark:text-slate-400">
                              {index + 1}
                            </td>

                            <td className="p-4">
                              {getPhoto(user.photo) ? (
                                <img
                                  src={getPhoto(user.photo)}
                                  alt={user.name || "İstifadəçi şəkli"}
                                  className="w-12 h-12 rounded-full object-cover border-2 border-slate-200 dark:border-slate-700"
                                />
                              ) : (
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white">
                                  <User className="w-5 h-5" />
                                </div>
                              )}
                            </td>

                            <td className="p-4">
                              <div className="font-bold">
                                {user.name || "Ad göstərilməyib"}
                              </div>

                              {user.referralCode && (
                                <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                  Referans kodu: {user.referralCode}
                                </div>
                              )}
                            </td>

                            <td className="p-4">
                              <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                                <Mail className="w-4 h-4 text-slate-400" />
                                <span>{user.email || "E-poçt göstərilməyib"}</span>
                              </div>
                            </td>

                            <td className="p-4">
                              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800">
                                {user.rank || "Rütbə göstərilməyib"}
                              </span>
                            </td>

                            <td className="p-4">
                              <span className="inline-flex items-center gap-2 font-bold text-emerald-600 dark:text-emerald-400">
                                <CheckCircle2 className="w-4 h-4" />
                                {formatSalary(user.totalUserSalary)} ₼
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="p-12">
                            <div className="text-center">
                              <Search className="w-10 h-10 mx-auto text-slate-400 mb-4" />

                              <h3 className="text-lg font-bold">
                                Nəticə tapılmadı
                              </h3>

                              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                                Seçilmiş dövr üçün axtarışınıza uyğun istifadəçi
                                tapılmadı.
                              </p>

                              {searchQuery && (
                                <button
                                  type="button"
                                  onClick={() => setSearchQuery("")}
                                  className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition"
                                >
                                  <Search className="w-4 h-4" />
                                  Axtarışı təmizlə
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* CƏDVƏLİN ALT MƏLUMATI */}
          {!usersLoading &&
            !usersError &&
            filteredSalaries.length > 0 && (
              <div className="px-5 sm:px-6 py-4 border-t border-slate-200 dark:border-white/10 bg-slate-50/70 dark:bg-slate-800/40">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm">
                  <p className="text-slate-500 dark:text-slate-400">
                    Göstərilən istifadəçi sayı:{" "}
                    <span className="font-bold text-slate-900 dark:text-white">
                      {filteredSalaries.length}
                    </span>
                  </p>

                  <p className="text-slate-500 dark:text-slate-400">
                    Seçilmiş dövr:{" "}
                    <span className="font-bold text-slate-900 dark:text-white">
                      {selectedPeriod || "Seçilməyib"}
                    </span>
                  </p>
                </div>
              </div>
            )}
        </section>
      </div>
    </div>
  );
};

export default Maas;

